/**
 * Shared helpers for map-related Vuex modules (swapMap, suggestionsMap).
 */
import { SWAP_FILTER_TYPES } from './swapList';

/**
 * The per-tab suggestions pins endpoint, shared by every map entry point
 * (side panel + fullscreen map buttons) so the URLs live in one place.
 * null = the top-picks default resolved inside suggestionsMap/fetchPins.
 */
export function getPinsEndpoint(swapFilterType) {
  switch (swapFilterType) {
    // The unanswered tab shows one combined feed (within + outside wishes),
    // so its pins must include the outside-wishes suggestions too. The
    // backend gates the flag on manual_directswaps, same as the list feed.
    case SWAP_FILTER_TYPES.UNANSWERED:
      return {
        url: '/api/suggestions/top-picks/{propositionId}/pins?includeOutsideWishes=true'
      };
    case SWAP_FILTER_TYPES.LAST_PART_NEW:
      return { url: '/api/suggestions/incoming/{propositionId}/pins' };
    case SWAP_FILTER_TYPES.ANSWERED:
      return { url: '/api/suggestions/interested/{propositionId}/pins' };
    case SWAP_FILTER_TYPES.REMOVED:
      return { url: '/api/suggestions/removed/{propositionId}/pins' };
    default:
      return null;
  }
}

/**
 * Transform compact pin API response to marker objects.
 * API format: { c: [lat, lng], i: [propositionId, ...], o: [outside ids] }
 * `o` lists the grouped propositions that are outside the user's wishes
 * (omitted when none). isOutsideWishes marks a fully-outside pin so an
 * alternative pin style can hook in once UX has decided one — a mixed
 * group keeps the default style.
 */
export function pinsToMarkers(items) {
  return items
    .filter(item => item.c && item.c.length >= 2 && item.i && item.i.length > 0)
    .map(item => ({
      id: item.i[0],
      propositionIds: item.i,
      count: item.i.length,
      outsideIds: item.o || [],
      isOutsideWishes: !!item.o && item.o.length === item.i.length,
      // `s` lists the grouped propositions with super interest (marker card
      // only; the pins keep their plain look).
      superIds: item.s || [],
      coordinate: {
        latitude: item.c[0],
        longitude: item.c[1]
      }
    }));
}

/**
 * Transform a swap API response into a normalised object
 * suitable for map marker cards.
 */
export function transformSwapResponse(swapResponse) {
  const otherParty = swapResponse.swapParts[1];
  return {
    propositionId: otherParty.propositionId,
    residences: otherParty.residences,
    isNew: swapResponse.isNew || false,
    hasBoost: swapResponse.hasBoost || false,
    // Per-field deviation vs the user's wishes (rent/rooms/sqm/...), engine-
    // computed and only present on outside-wishes swaps. Drives the deviation
    // dots on the map marker card, same as `deviations` on the list cards.
    deviations: otherParty.diff || null,
    // Either party superliked (set by the backend on triangle details today).
    hasSuperInterest: swapResponse.hasSuperInterest === true,
    details: {
      type: swapResponse.type,
      interests: swapResponse.swapParts.map(sp => sp.interested)
    }
  };
}
